'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';

import { NON_G2RS_FEATURE_FLAG } from '@/config/non-g2rs-mode-config';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { useSession } from 'next-auth/react';
import { Badge, Box, Button, ButtonBase, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { Sparkles } from 'lucide-react';

import { authConfig } from '@/config/auth-config';
import { ChatWorkspace, type ChatActiveConversation } from '@/components/chat/chat-prototype';

export type CopilotoWindowState = 'closed' | 'minimized' | 'open' | 'expanded';

export type CopilotoWindowSize = { width: number; height: number };

export type CopilotoStoredState = {
  state: CopilotoWindowState;
  conversationId: string | null;
  size?: CopilotoWindowSize | null;
};

export const COPILOTO_WINDOW_STORAGE_KEY = 'copiloto-window-state';

/**
 * The Copiloto stays out of pages that own their own chat/admin surfaces;
 * exact match for /chat and prefix match for the rest (GoogleOneTapPrompt
 * pattern).
 */
const HIDDEN_COPILOTO_ROUTE_PREFIXES = [
  '/admin',
  '/authorize',
  '/mcp',
  '/reviewer',
  '/email',
  '/metrics',
  '/carteiras',
];

export const isCopilotoHiddenRoute = (pathname: string) => {
  if (pathname === '/chat') return true;
  return HIDDEN_COPILOTO_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
};

const WINDOW_STATES: readonly CopilotoWindowState[] = ['closed', 'minimized', 'open', 'expanded'];

export const transitionCopilotoWindow = (
  current: CopilotoWindowState,
  action: 'open' | 'minimize' | 'close' | 'toggle-expand',
): CopilotoWindowState => {
  switch (action) {
    case 'open':
      return 'open';
    case 'minimize':
      return current === 'closed' ? 'closed' : 'minimized';
    case 'close':
      return 'closed';
    case 'toggle-expand':
      return current === 'expanded' ? 'open' : 'expanded';
  }
};

export const COPILOTO_MIN_WIDTH = 360;
export const COPILOTO_MIN_HEIGHT = 420;

/** Keeps the dragged window inside usable viewport bounds. */
export const clampCopilotoSize = (
  width: number,
  height: number,
  viewportWidth: number,
  viewportHeight: number,
): CopilotoWindowSize => {
  const maxWidth = Math.max(COPILOTO_MIN_WIDTH, viewportWidth - 48);
  const maxHeight = Math.max(COPILOTO_MIN_HEIGHT, viewportHeight - 24);
  return {
    width: Math.min(Math.max(Math.round(width), COPILOTO_MIN_WIDTH), maxWidth),
    height: Math.min(Math.max(Math.round(height), COPILOTO_MIN_HEIGHT), maxHeight),
  };
};

const isValidSize = (value: unknown): value is CopilotoWindowSize =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as CopilotoWindowSize).width === 'number' &&
  Number.isFinite((value as CopilotoWindowSize).width) &&
  (value as CopilotoWindowSize).width > 0 &&
  typeof (value as CopilotoWindowSize).height === 'number' &&
  Number.isFinite((value as CopilotoWindowSize).height) &&
  (value as CopilotoWindowSize).height > 0;

export const readStoredCopilotoState = (storage?: Pick<Storage, 'getItem'>): CopilotoStoredState => {
  const fallback: CopilotoStoredState = { state: 'closed', conversationId: null, size: null };
  let raw: string | null = null;
  try {
    raw = (storage ?? window.localStorage).getItem(COPILOTO_WINDOW_STORAGE_KEY);
  } catch {
    return fallback;
  }
  if (!raw) return fallback;
  try {
    const parsed = JSON.parse(raw) as { state?: unknown; conversationId?: unknown; size?: unknown };
    const state = WINDOW_STATES.find((candidate) => candidate === parsed.state);
    return {
      state: state ?? 'closed',
      conversationId: typeof parsed.conversationId === 'string' && parsed.conversationId ? parsed.conversationId : null,
      size: isValidSize(parsed.size) ? parsed.size : null,
    };
  } catch {
    return fallback;
  }
};

export const writeStoredCopilotoState = (state: CopilotoStoredState, storage?: Pick<Storage, 'setItem'>) => {
  try {
    (storage ?? window.localStorage).setItem(COPILOTO_WINDOW_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage can be unavailable (private mode); the launcher still works unpersisted.
  }
};

const pillSx = {
  position: 'fixed',
  right: { xs: 16, md: 24 },
  bottom: { xs: 16, md: 24 },
  zIndex: (theme: { zIndex: { speedDial: number } }) => theme.zIndex.speedDial,
  borderRadius: 999,
  px: 2,
  py: 1.15,
  textTransform: 'none' as const,
  fontWeight: 850,
  color: 'hsl(var(--primary-foreground))',
  bgcolor: 'hsl(var(--primary))',
  boxShadow: '0 14px 30px hsl(var(--primary) / 0.28)',
  '&:hover': { bgcolor: 'hsl(var(--primary) / 0.9)' },
};

const CopilotoSpark = ({ size = 22 }: { size?: number }) => (
  <Box
    sx={{
      width: size + 22,
      height: size + 22,
      display: 'grid',
      placeItems: 'center',
      borderRadius: 2,
      flexShrink: 0,
      bgcolor: 'hsl(var(--primary))',
      color: 'hsl(var(--primary-foreground))',
    }}
  >
    <Sparkles size={size} aria-hidden='true' />
  </Box>
);

function GuestCopilotoPanel({ onClose }: { onClose: () => void }) {
  const loginHref = authConfig.loginUrlForRedirect('/chat');
  return (
    <Stack spacing={1.5} sx={{ p: { xs: 2.5, md: 3.5 }, alignItems: 'flex-start', overflow: 'auto' }}>
      <CopilotoSpark />
      <Typography variant='h6' fontWeight={850}>
        Copiloto
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        Seu assistente de investimentos do Análise de FIIs: fundos, carteira, mercado e cursos, com os dados reais da
        plataforma. Entre para conversar.
      </Typography>
      <Stack direction='row' spacing={1} sx={{ mt: 1 }}>
        <Button variant='contained' href={loginHref} LinkComponent={Link} onClick={onClose}>
          Entrar para conversar
        </Button>
        <Button component={Link} href='/chat' variant='text' onClick={onClose}>
          Copiloto completo
        </Button>
      </Stack>
    </Stack>
  );
}

type ResizeMode = 'width' | 'height' | 'corner';

const resizeHandleSx = (cursor: string) => ({
  position: 'absolute' as const,
  touchAction: 'none' as const,
  cursor,
  zIndex: 3,
  display: { xs: 'none', md: 'block' },
});

export default function CopilotoLauncher() {
  const pathname = usePathname();
  const { status } = useSession();
  const { ready, isEnabled } = useFeatureFlags();
  const isAuthenticated = status === 'authenticated';
  const [hydrated, setHydrated] = useState(false);
  const [windowState, setWindowState] = useState<CopilotoWindowState>('closed');
  const [activeConversation, setActiveConversation] = useState<ChatActiveConversation | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [customSize, setCustomSize] = useState<CopilotoWindowSize | null>(null);
  // The workspace mounts once the window is first opened (or restored) and
  // then stays mounted hidden, so a streamed answer survives minimize/close.
  const [workspaceActivated, setWorkspaceActivated] = useState(false);
  const windowStateRef = useRef<CopilotoWindowState>('closed');
  const resizeStartRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  const dispatch = useCallback((action: 'open' | 'minimize' | 'close' | 'toggle-expand') => {
    if (action === 'open' || action === 'toggle-expand') setWorkspaceActivated(true);
    setWindowState((current) => {
      const next = transitionCopilotoWindow(current, action);
      windowStateRef.current = next;
      if (next === 'open' || next === 'expanded') setUnreadCount(0);
      return next;
    });
  }, []);

  useEffect(() => {
    const stored = readStoredCopilotoState();
    windowStateRef.current = stored.state;
    setWindowState(stored.state);
    if (stored.conversationId) {
      setWorkspaceActivated(true);
      setActiveConversation({ id: stored.conversationId, title: 'Conversa' });
    }
    if (stored.size) setCustomSize(stored.size);
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    writeStoredCopilotoState({
      state: windowState,
      conversationId: activeConversation?.id ?? null,
      size: customSize,
    });
  }, [hydrated, windowState, activeConversation, customSize]);

  const handleActiveConversationChange = useCallback((conversation: ChatActiveConversation | null) => {
    setActiveConversation((current) =>
      current?.id === conversation?.id && current?.title === conversation?.title ? current : conversation,
    );
  }, []);

  const handleActivity = useCallback(() => {
    if (windowStateRef.current === 'open' || windowStateRef.current === 'expanded') return;
    setUnreadCount((count) => count + 1);
  }, []);

  const startResize = (event: React.PointerEvent) => {
    event.preventDefault();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    resizeStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      width: customSize?.width ?? 430,
      height: customSize?.height ?? 640,
    };
  };

  const moveResize = (event: React.PointerEvent, mode: ResizeMode) => {
    const start = resizeStartRef.current;
    if (!start) return;
    // The window is anchored to the bottom-right corner: dragging left/up
    // grows width/height.
    const dx = start.x - event.clientX;
    const dy = start.y - event.clientY;
    setCustomSize(
      clampCopilotoSize(
        mode === 'height' ? start.width : start.width + dx,
        mode === 'width' ? start.height : start.height + dy,
        window.innerWidth,
        window.innerHeight,
      ),
    );
  };

  const endResize = () => {
    resizeStartRef.current = null;
  };

  const resizeHandle = (mode: ResizeMode, cursor: string, style: object) => (
    <Box
      data-copiloto-resize={mode}
      aria-hidden='true'
      onPointerDown={(event) => {
        event.preventDefault();
        startResize(event);
      }}
      onPointerMove={(event) => moveResize(event, mode)}
      onPointerUp={endResize}
      onPointerCancel={endResize}
      sx={{ ...resizeHandleSx(cursor), ...style }}
    />
  );

  if (status === 'loading' || isCopilotoHiddenRoute(pathname)) return null;
  if (!ready || !isEnabled(NON_G2RS_FEATURE_FLAG)) return null;

  const isWindowOpen = windowState === 'open' || windowState === 'expanded';
  const renderWorkspace = isAuthenticated && hydrated && workspaceActivated;

  return (
    <>
      {windowState === 'closed' ? (
        <Tooltip title='Abrir Copiloto' placement='left'>
          <Button
            onClick={() => dispatch('open')}
            startIcon={<Sparkles size={17} />}
            sx={pillSx}
            data-copiloto-launcher
          >
            <Badge
              color='error'
              badgeContent={unreadCount}
              invisible={unreadCount === 0}
              sx={{ '& .MuiBadge-badge': { right: -10, top: -6 } }}
            >
              Copiloto
            </Badge>
          </Button>
        </Tooltip>
      ) : null}
      {windowState === 'minimized' ? (
        <ButtonBase
          onClick={() => dispatch('open')}
          data-copiloto-launcher
          sx={{
            ...pillSx,
            maxWidth: { xs: 'calc(100vw - 32px)', md: 360 },
            gap: 1,
          }}
        >
          <Badge
            color='error'
            badgeContent={unreadCount}
            invisible={unreadCount === 0}
            sx={{ '& .MuiBadge-badge': { right: -8, top: -4 } }}
          >
            <Sparkles size={17} />
          </Badge>
          <Typography component='span' variant='body2' noWrap sx={{ fontWeight: 850, color: 'inherit' }}>
            Copiloto · {activeConversation?.title ?? 'conversa'}
          </Typography>
        </ButtonBase>
      ) : null}
      {hydrated && (isWindowOpen || renderWorkspace) ? (
        <Paper
          elevation={0}
          data-copiloto-window
          sx={{
            position: 'fixed',
            right: { xs: 0, md: 24 },
            bottom: 0,
            zIndex: (theme) => theme.zIndex.speedDial,
            width: {
              xs: '100%',
              md: windowState === 'expanded' ? 'min(900px, 92vw)' : customSize ? `${customSize.width}px` : 430,
            },
            height: {
              xs: '78vh',
              md:
                windowState === 'expanded'
                  ? 'min(860px, 88vh)'
                  : customSize
                    ? `${customSize.height}px`
                    : 'min(640px, 82vh)',
            },
            maxWidth: { md: '96vw' },
            maxHeight: { md: '94vh' },
            borderRadius: { xs: '14px 14px 0 0', md: 3 },
            border: '1px solid hsl(var(--border))',
            bgcolor: 'hsl(var(--background))',
            color: 'hsl(var(--foreground))',
            overflow: 'hidden',
            display: isWindowOpen ? 'flex' : 'none',
            flexDirection: 'column',
            boxShadow: '0 24px 70px hsl(var(--foreground) / 0.18)',
          }}
        >
          {windowState === 'open' ? (
            <>
              {resizeHandle('width', 'ew-resize', { left: 0, top: 16, bottom: 16, width: 10 })}
              {resizeHandle('height', 'ns-resize', { top: 0, left: 16, right: 16, height: 10 })}
              {resizeHandle('corner', 'nwse-resize', { top: 0, left: 0, width: 18, height: 18 })}
            </>
          ) : null}
          {renderWorkspace ? (
            <ChatWorkspace
              variant='drawer'
              onClose={() => dispatch('close')}
              onMinimize={() => dispatch('minimize')}
              onToggleExpand={() => dispatch('toggle-expand')}
              isExpanded={windowState === 'expanded'}
              initialConversationId={activeConversation?.id ?? null}
              onActiveConversationChange={handleActiveConversationChange}
              onActivity={handleActivity}
            />
          ) : (
            <GuestCopilotoPanel onClose={() => dispatch('close')} />
          )}
        </Paper>
      ) : null}
    </>
  );
}
